import { effect } from "@preact/signals";
import * as _ from "underscore";

import ReadingTracker from "ReadingTracker";
import * as analytics from "analytics";
import * as audioPlayer from "audioPlayer";
import * as chooseFirstLanguageDialog from "chooseFirstLanguageDialog";
import * as currentDate from "currentDate";
import * as notificationBar from "notificationBar";
import type * as readerTokens from "readerText/tokens";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";
import type ReaderTextStore from "stores/ReaderTextStore";
import ReaderViewStore from "stores/ReaderViewStore";
import UserWordStore from "stores/UserWordStore";
import stores from "stores/stores";
import * as translate from "translate";
import * as tts from "tts";
import type { ReaderFooterState } from "views/ReaderFooterView";
import type { WordPopUpOptions } from "views/WordPopUpView";
import * as phraseHighlighter from "../readerPagePhraseHighlighter";
import ReaderTextLayout from "./readerTextLayout";

type ReaderTextControllerDeps = {
  getAppRouter: () => any;
  getDictionaryManager: () => any;
  getIsSyncAudioPlaying: () => boolean;
  getOptions: () => any;
  getPageData: () => any;
  getReadAloudController: () => any;
  getReaderTextStore: () => ReaderTextStore | undefined;
  getReaderViewStore: () => ReaderViewStore | undefined;
  getRenderedCallback: () => ((renderedOptions?: any) => void) | undefined;
  /**
   * Reading-guide hook: shows a popover for the glossary entry matching the
   * selected text, returning false when there's no match. When it returns
   * true the popover must eventually call exactly one of `dismiss` or
   * `translate` (see ReaderTextStore's `glossaryLookup` option).
   */
  lookupGuideEntry: (
    text: string,
    actions: {
      range: readerTokens.PositionRange;
      neighborWord: (offset: number) => string | undefined;
      dismiss: () => void;
      translate: () => void;
    }
  ) => boolean;
  maybeShowExplainNudge: (userWord: any) => void;
  onPageRendered: () => void;
  removeWordPopUp: () => void;
  setPageTextViewportNoFooter: (noFooter: boolean) => void;
  setRenderedCallback: (
    renderedCallback: ((renderedOptions?: any) => void) | undefined
  ) => void;
  showWordPopUp: (wordPopUpOptions: WordPopUpOptions) => void;
  updateFooterState: (footerState: ReaderFooterState) => void;
};

/**
 * Orchestrates the reader text page: wires up the interaction store
 * (translator, line nudge, per-page subscriptions), audio position
 * highlighting, and reading tracking. Pagination and all span geometry live
 * in ReaderTextLayout, which this controller instantiates and delegates to.
 */
export default class ReaderTextController {
  private disposeAudioMapEffect?: () => void;
  private readingTracker?: ReadingTracker;
  private savedRenderedCallback?: (renderedOptions?: any) => void;
  private textInteractionDisposers: Array<() => void> = [];
  private audioHighlightFadeTimer?: ReturnType<typeof setTimeout>;
  private lastHighlightedAudioPosition?: number;

  private readonly layout: ReaderTextLayout;

  constructor(private deps: ReaderTextControllerDeps) {
    this.layout = new ReaderTextLayout({
      getAppRouter: () => deps.getAppRouter(),
      getOptions: () => deps.getOptions(),
      getReaderTextStore: () => deps.getReaderTextStore(),
      getReaderViewStore: () => deps.getReaderViewStore(),
      pageRendered: () => this.pageRendered(),
      setPageTextViewportNoFooter: (noFooter) =>
        deps.setPageTextViewportNoFooter(noFooter),
      setupSyncAudioPlayerIfNecessary: () =>
        this.setupSyncAudioPlayerIfNecessary(),
      updateDictionarySize: () =>
        deps.getDictionaryManager()?.updateDictionarySize(),
      updateFooterState: (footerState) => deps.updateFooterState(footerState),
      updateUserBookPosition: (position, readTo) =>
        this.updateUserBookPosition(stores.bookModel, position, readTo),
    });
  }

  // ---------------------------------------------------------------------
  // Layout delegation (pagination lives in ReaderTextLayout)
  // ---------------------------------------------------------------------

  setLayoutDisabled(updateLayoutDisabled: boolean) {
    this.layout.setLayoutDisabled(updateLayoutDisabled);
  }

  areNavigationButtonsEnabled() {
    return this.layout.areNavigationButtonsEnabled();
  }

  hasPageTextViewport() {
    return this.layout.hasPageTextViewport();
  }

  setupReaderMode(options: any) {
    this.layout.setupReaderMode(options);
  }

  finishReaderPageLayout() {
    this.layout.finishReaderPageLayout();
  }

  onLastPage() {
    return this.layout.onLastPage();
  }

  updateLayout = (retainPosition?: number, doNotRemoveFirstLine?: boolean) => {
    this.layout.updateLayout(retainPosition, doNotRemoveFirstLine);
  };

  /**
   * Audio books (re-)initialize their player whenever the layout runs, so the
   * player follows page geometry changes. Invoked by ReaderTextLayout.
   */
  private setupSyncAudioPlayerIfNecessary() {
    const audioFileID = stores.bookModel.book.value.audioFileID;

    if (audioFileID) {
      audioPlayer.setupIfNecessary({
        audioFileID,
        onPositionUpdate: (position: number) =>
          this.deps.getReadAloudController()?.onAudioPositionUpdated(position),
        onBufferingChange: (isBuffering: boolean) =>
          this.deps
            .getReadAloudController()
            ?.getReadAloudButton()
            ?.setIsBuffering(isBuffering),
        audioMap: stores.bookModel.book.value.audioMap,
      });

      const updateAudioMap = () => {
        audioPlayer.updateAudioMap(stores.bookModel.book.value.audioMap);
      };

      this.disposeAudioMapEffect?.();
      this.disposeAudioMapEffect = effect(() => {
        void stores.bookModel.book.value.audioMap;
        updateAudioMap();
      });
    }
  }

  resetForPrepare() {
    this.readingTracker = undefined;
  }

  clearAudioPositionHighlight() {
    clearTimeout(this.audioHighlightFadeTimer);
    this.audioHighlightFadeTimer = undefined;
    this.lastHighlightedAudioPosition = undefined;
    this.deps.getReaderTextStore()?.setAudioPosition(undefined);
  }

  highlightAudioPosition(position: number) {
    if (this.lastHighlightedAudioPosition === position) {
      return;
    }
    clearTimeout(this.audioHighlightFadeTimer);

    // Voices that don't fire word-by-word boundary events leave a sentence's
    // final word highlighted until the next sentence starts (possibly across a
    // page flip). Auto-fade the highlight if no new position arrives. The
    // body class scopes the CSS fade transition to these voices only.
    const supportsBoundaries = tts.getVoiceBoundarySupport({
      user: stores.user,
      feature: "readAloud",
      language: stores.bookModel.book.value.language,
    });
    document.body.classList.toggle(
      "audioHighlightFade",
      supportsBoundaries === false
    );

    this.lastHighlightedAudioPosition = position;
    this.deps.getReaderTextStore()?.setAudioPosition(position);

    if (supportsBoundaries === false) {
      this.audioHighlightFadeTimer = setTimeout(() => {
        this.deps.getReaderTextStore()?.setAudioPosition(undefined);
        this.audioHighlightFadeTimer = undefined;
      }, 1500);
    }
  }

  getSavedRenderedCallback() {
    return this.savedRenderedCallback;
  }

  getFirstSpanStartPosition() {
    return this.getReaderViewStore().firstPosition.value ?? NaN;
  }

  getLastSpanEndPosition() {
    return this.getReaderViewStore().lastPosition.value ?? NaN;
  }

  isAtEndOfBookLastSpan() {
    const lastWordPosition =
      this.deps.getReaderTextStore()?.tokens.value?.lastWordPosition;
    return (
      this.deps.getPageData().endOfBook &&
      lastWordPosition !== undefined &&
      this.getReaderViewStore().lastPosition.value === lastWordPosition
    );
  }

  setupPageTextIsland(options: any) {
    this.getReaderViewStore().setTextVisible(false);

    if (!options.endOfText) {
      const readAloudController = this.deps.getReadAloudController();
      if (readAloudController?.getTTSPosition()) {
        readAloudController.highlightTTSPosition();
      }
    }
    this.layout.setLayoutDisabled(false);
  }

  setupTextInteractions(options: any) {
    this.disposeTextInteractionSubscriptions();

    const store = this.getReaderTextStore();
    const readerViewStore = this.getReaderViewStore();

    phraseHighlighter.initializePhraseHighlighter({
      readerTextStore: store,
      readerViewStore,
      updateDictionary: (dictOptions) =>
        this.deps.getDictionaryManager()?.updateDictionary(dictOptions),
      setPreviousNewContext: (context) => {
        this.deps.getDictionaryManager()?.setPreviousNewContext(context);
      },
      readAloudButton: this.deps.getReadAloudController()?.getReadAloudButton(),
      showWordPopUp: this.deps.showWordPopUp,
      isSyncAudioPlaying: this.deps.getIsSyncAudioPlaying,
    });

    store.setup({
      sourceLanguage: stores.bookModel.book.value.language,
      scrollMode: "scroll" in options,
      bookModel: stores.bookModel,
      stores,
      beforeTranslate: () => {
        this.deps.removeWordPopUp();
      },
      glossaryLookup: (text, actions) =>
        this.deps.lookupGuideEntry(text, actions),
      translateDictionaryLookup: async (userWord, range) => {
        // Note 2023-06-02: This gets called after the UserWord is saved to the server, so there's a
        // slight lag even after the translation hint appears.
        this.deps.getDictionaryManager()?.updateDictionary({
          userWord,
          position: range.from,
        });
        this.updateLayout(range.from, false);

        this.trackReadingInteraction(range.to);

        this.deps.maybeShowExplainNudge(userWord);
      },
      translator: translate.getTranslator({
        stores: stores,
        notify: notificationBar.showMessage,
        dismissNotification: notificationBar.dismiss,
      }),
      updateLayout: (retainPosition?: number) => {
        this.updateLayout(retainPosition);
      },
      lineNudger: {
        shouldShiftForRange: this.layout.shouldShiftForRange,
        shiftPageDownOneLine: this.layout.shiftPageDownOneLine,
      },
      speak: (language, word) => {
        const readAloudButton = this.deps
          .getReadAloudController()
          ?.getReadAloudButton();
        if (
          !readAloudButton?.isPlaying &&
          stores.user.speakingModeEnabled(language) &&
          !this.deps.getIsSyncAudioPlaying()
        ) {
          // eslint-disable-next-line no-irregular-whitespace
          if (word.split(/[  \t.\n\r:;]/).length <= 12) {
            tts.play({
              user: stores.user,
              feature: "speakingMode",
              language,
              text: word,
            });
          }
        }
      },
      userWordCreator: function (wordDefinition) {
        return new UserWordStore(wordDefinition);
      },
      deleteOnUntranslate:
        stores.user.state.value.readerDeselectBehavior === "delete",
      contextBeforePage: this.deps.getPageData().contextBeforePage,
      chooseFirstLanguage: chooseFirstLanguageDialog.show,
    });

    phraseHighlighter.indexTokens();
    phraseHighlighter.updateAutoHighlights();

    let previousBookLanguage = stores.bookModel.book.value.language;
    this.textInteractionDisposers.push(
      effect(() => {
        const bookLanguage = stores.bookModel.book.value.language;
        if (
          previousBookLanguage !== undefined &&
          bookLanguage !== previousBookLanguage
        ) {
          // Simple way to force updating of both dictionary and browser TTS options
          window.location.reload();
        }
        previousBookLanguage = bookLanguage;
      }),
      stores.user.onChange((a) => a.firstLanguage, this.onFirstLanguageChanged),
      stores.user.onChange(
        (a) => a.readerWordHighlights,
        () => {
          phraseHighlighter.resetUpsellFlag();
        }
      ),
      stores.user.onChange(
        (a) => a.readerDeselectBehavior,
        this.onChangeReaderDeselectBehavior
      ),
      stores.user.onChange(
        (a) => a.readerReview,
        () => {
          window.location.reload();
        }
      )
    );

    this.onFirstLanguageChanged(stores.user.firstLanguage.value);
  }

  trackSyncAudioPosition(position: number) {
    if (!this.readingTracker) {
      return;
    }

    this.trackReadingInteraction(position);
    this.logWordsRead(this.readingTracker.getStats());

    // Reset readingTracker so as not to double count
    this.resetReadingTracker(position);
  }

  updateSyncAudioUserBookPosition(position: number) {
    this.updateUserBookPosition(stores.bookModel, position, undefined);
  }

  async trackFinishPage() {
    if (!this.readingTracker) {
      return;
    }

    const readerViewStore = this.getReaderViewStore();
    // Track last interaction on the last span
    const lastPosition = readerViewStore.lastPosition.value;
    if (lastPosition !== undefined) {
      this.trackReadingInteraction(lastPosition);
    }

    const readingStats = this.readingTracker.getStats();

    if (readingStats.fractionWordsRead < 0.67) {
      // Didn't read more than 2/3 of the words, so don't track
    } else {
      // Track reading stats
      const seconds = Math.round(readingStats.timeReading / 1000);

      // XXX Rename WPM to readingSpeed for consistency
      stores.user.updateReadingSpeed(
        stores.bookModel.book.value.language,
        readingStats.WPM
      );

      this.logWordsRead(readingStats);

      analytics.trackEventWithProperties("pageFinish", {
        Words: readingStats.wordsRead,
        Time: seconds,
        "Reading Speed": readingStats.wordsRead / (seconds / 60),
        "Fraction Read": readingStats.fractionWordsRead,
        ...this.pageTrackingProperties(),
      });
    }

    this.readingTracker = undefined;
  }

  close() {
    this.trackFinishPage();
    this.layout.close();
    this.disposeAudioMapEffect?.();
    this.disposeAudioMapEffect = undefined;
    this.disposeTextInteractionSubscriptions();
    phraseHighlighter.disableHighlightAllPhrases();
    this.deps.getReaderTextStore()?.close();
  }

  private disposeTextInteractionSubscriptions() {
    for (const dispose of this.textInteractionDisposers) {
      dispose();
    }
    this.textInteractionDisposers.length = 0;
  }

  private numberOfWordsInPage() {
    const readerViewStore = this.getReaderViewStore();
    return (
      (readerViewStore.lastPosition.value ?? 0) -
      (readerViewStore.firstPosition.value ?? 0)
    );
  }

  private pageTrackingProperties() {
    return {
      "Words On Page": this.numberOfWordsInPage(),
      Public: stores.bookModel.book.value.public,
      Category: stores.bookModel.book.value.category,
      Type: stores.bookModel.book.value.youTubeID ? "video" : "text",
      "Owner ID": stores.bookModel.book.value.ownerID,
      "Total Words": stores.bookModel.book.value.totalWords,
      "Reader Font": stores.user.state.value.readerFont,
      "Reader Translation Style":
        stores.user.state.value.readerTranslationStyle,
      "Reader Word Highlights": stores.user.state.value.readerWordHighlights,
    };
  }

  private pageRendered() {
    this.deps.onPageRendered();

    const renderedCallback = this.deps.getRenderedCallback();
    if (renderedCallback) {
      renderedCallback();
      this.savedRenderedCallback = renderedCallback;
      this.deps.setRenderedCallback(undefined);
    }

    // Flash highlight for search result navigation
    const pendingHighlight = (window as any).__pendingSearchHighlight;
    if (pendingHighlight != null) {
      delete (window as any).__pendingSearchHighlight;
      this.deps.getReaderTextStore()?.flashSearchPosition(pendingHighlight);
    }

    // Create reading tracker and track page start
    const readerViewStore = this.getReaderViewStore();
    if (
      !this.readingTracker &&
      readerViewStore.firstRange.value !== undefined &&
      readerViewStore.lastRange.value !== undefined
    ) {
      this.resetReadingTracker(readerViewStore.firstPosition.value ?? 0);
      analytics.trackEventWithProperties(
        "pageStart",
        this.pageTrackingProperties()
      );
    }
  }

  private updateUserBookPosition = _.throttle(
    async (bookModel, position, readTo) => {
      const date = await currentDate.get();

      const userBookPatch = {
        position: position,
        readTo: readTo || position,
        recentlyReadTo: readTo || position,
        lastReadDate: date.toISOString(),
      };

      const userBook = bookModel.book.value.userBook;

      if (userBook) {
        if (
          userBookPatch.position !== userBook.position ||
          (userBook.readTo && userBookPatch.readTo !== userBook.readTo) ||
          (userBook.recentlyReadTo &&
            userBookPatch.recentlyReadTo !== userBook.recentlyReadTo) ||
          (userBook.lastReadDate &&
            new Date(userBookPatch.lastReadDate).getTime() -
              new Date(userBook.lastReadDate).getTime() >
              2 * 60 * 1000)
        ) {
          userBookPatch.readTo = Math.max(
            userBookPatch.readTo,
            // Using truthiness of readTo to catch potential NaN
            userBook.readTo ? userBook.readTo : 0
          );
        }
        bookModel.patchUserBook(userBookPatch);
      }
    },
    1500
  );

  private onFirstLanguageChanged = (value) => {
    this.deps.getDictionaryManager()?.updateDictionary({});
    document.body.classList.toggle(
      "basicLatinFirstLanguage",
      languageMap.writtenInBasicLatin(value)
    );
  };

  private onChangeReaderDeselectBehavior = () => {
    this.deps
      .getReaderTextStore()
      ?.setDeleteOnUntranslate(
        stores.user.state.value.readerDeselectBehavior === "delete"
      );
  };

  private trackReadingInteraction(position: number) {
    if (!this.readingTracker) {
      return;
    }
    this.readingTracker.reportInteraction(position);
  }

  private async logWordsRead(readingStats) {
    if (!(await readlangClient.isLocalMode())) {
      const language = stores.bookModel.book.value.language;
      // Send tracking info
      readlangClient.request(
        "POST",
        "/api/userLearningLog/read",
        {
          wordsRead: readingStats.wordsRead,
          timeTaken: Math.round(readingStats.timeReading / 1000),
          language,
          timezoneOffset: dateUtils.getTimezoneOffset(),
        },
        async () => {
          if (await stores.user.hasNewStreakFeature()) {
            stores.streak.fetch();
          }
        }
      );
      stores.user.markLanguageActive(language);
    }
  }

  private resetReadingTracker(position: number) {
    this.readingTracker = new ReadingTracker(
      position,
      Date.now(),
      languageMap.map[stores.bookModel.book.value.language].noWordBoundaries
        ? 0.5
        : stores.bookModel.book.value.metrics?.words
          ? stores.bookModel.book.value.metrics.words /
            stores.bookModel.book.value.totalWords
          : 1
    );
  }

  private getReaderViewStore() {
    const readerViewStore = this.deps.getReaderViewStore();
    if (!readerViewStore) {
      throw new Error("Reader text controller requires a reader view store");
    }
    return readerViewStore;
  }

  private getReaderTextStore() {
    const readerTextStore = this.deps.getReaderTextStore();
    if (!readerTextStore) {
      throw new Error("Reader text controller requires a reader text store");
    }
    return readerTextStore;
  }
}
